import en from '../assets/i18n/en.json';
import es from '../assets/i18n/es.json';

type Language = 'en' | 'es';

const resources: Record<Language, any> = { en, es };
const supportedLanguages = Object.keys(resources) as Language[];
const defaultLanguage: Language = 'en';

// El idioma llega por la URL (?lang=es | ?lang=en) y queda fijo durante toda
// la sesión: cambiarlo implica recargar, así que no hace falta un contexto de
// React. Fuera del navegador (tests) no hay window y se usa el idioma por
// defecto.
const getLanguageParam = (): string | null => {
  if(typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get('lang');
}

const detectLanguage = (): Language => {
  const lang = getLanguageParam()?.toLowerCase();
  return supportedLanguages.includes(lang as Language) ? lang as Language : defaultLanguage;
}

const language = detectLanguage();

const lookup = (lang: Language, key: string): any => {
  return key.split('.').reduce((node, part) => node?.[part], resources[lang]);
}

const interpolate = (text: string, vars?: Record<string, string | number>): string => {
  if(!vars) {
    return text;
  }
  return text.replace(/\{\{(\w+)\}\}/g, (match, name) => name in vars ? String(vars[name]) : match);
}

// Plurales en formato i18next: `key_one` / `key_other`, elegidos con `count`.
const resolveKey = (key: string, vars?: Record<string, string | number>): string => {
  if(typeof vars?.count !== 'number') {
    return key;
  }
  return `${key}_${new Intl.PluralRules(language).select(vars.count)}`;
}

// Si falta la clave en el idioma activo cae al inglés, y si tampoco existe
// devuelve la propia clave — así una traducción olvidada se ve, pero no rompe.
const t = (key: string, vars?: Record<string, string | number>): string => {
  const resolved = resolveKey(key, vars);
  const value = lookup(language, resolved) ?? lookup(defaultLanguage, resolved) ?? lookup(language, key) ?? lookup(defaultLanguage, key);
  return typeof value === 'string' ? interpolate(value, vars) : key;
}

const tList = (key: string): string[] => {
  const value = lookup(language, key) ?? lookup(defaultLanguage, key);
  return Array.isArray(value) ? value : [];
}

const tObject = (key: string): any => {
  return lookup(language, key) ?? lookup(defaultLanguage, key);
}

// Traduce un valor que llega en inglés desde la API (nombres de mes o de día
// de la semana, género, rango de edad, emoción). Lo que no reconoce se
// devuelve tal cual: horas, años, nombres de zonas, etc.
const translateLabel = (label: string): string => {
  if(language === defaultLanguage || typeof label !== 'string') {
    return label;
  }
  for(const listKey of [ 'dates.months', 'dates.weekdays' ]) {
    const index = (lookup(defaultLanguage, listKey) as string[]).indexOf(label);
    if(index >= 0) {
      return tList(listKey)[index];
    }
  }
  for(const groupKey of [ 'gender', 'age', 'emotion' ]) {
    const value = lookup(language, `${groupKey}.${label}`);
    if(typeof value === 'string') {
      return value;
    }
  }
  return label;
}

export { Language, language, getLanguageParam, t, tList, tObject, translateLabel };
